/**
 * Topbar — sticky header with breadcrumbs, search (cmd+K placeholder),
 * notifications bell with unread dot, theme toggle and avatar menu.
 */
import { Bell, Moon, Search, Sun, ChevronRight } from 'lucide-react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useEffect, useState } from 'react';
import { useRouterState, Link } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { useThemeStore } from '@/lib/theme';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';

interface TopbarProps {
  title: string;
  actions?: React.ReactNode;
  description?: string;
}

const ROUTE_LABEL: Record<string, string> = {
  dashboard: 'Dashboard',
  residents: 'Residents',
  towers: 'Towers & Flats',
  guards: 'Guards',
  'visitor-logs': 'Visitor logs',
  'frequent-visitors': 'Frequent visitors',
  notices: 'Notices',
  settings: 'Settings'
};

function buildCrumbs(pathname: string): { label: string; to: string }[] {
  const parts = pathname.split('/').filter(Boolean);
  const crumbs: { label: string; to: string }[] = [];
  let acc = '';
  for (const p of parts) {
    acc += `/${p}`;
    crumbs.push({ label: ROUTE_LABEL[p] ?? p, to: acc });
  }
  return crumbs;
}

export function Topbar({ title, actions, description }: TopbarProps): JSX.Element {
  const theme = useThemeStore((s) => s.theme);
  const toggle = useThemeStore((s) => s.toggle);
  const { logout, user } = useAuth();
  const { location } = useRouterState();
  const crumbs = buildCrumbs(location.pathname);

  // mock unread count — wire to /v1/notifications later.
  const unread = 3;

  // cmd+K stub — opens nothing yet, just focuses search input. Useful affordance.
  const [searchFocused, setSearchFocused] = useState(false);
  useEffect(() => {
    function handler(e: KeyboardEvent): void {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        const el = document.getElementById('vt-topbar-search') as HTMLInputElement | null;
        el?.focus();
      }
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  return (
    <header className="sticky top-0 z-20 border-b border-vt-border bg-vt-surface/85 backdrop-blur supports-[backdrop-filter]:bg-vt-surface/70">
      <div className="flex h-14 items-center gap-vt-4 px-vt-6">
        {/* Crumbs + page title */}
        <div className="min-w-0 flex-1">
          <nav aria-label="Breadcrumb" className="hidden h-4 items-center gap-1 text-vt-12 text-vt-text-muted md:flex">
            {crumbs.map((c, i) => (
              <span key={c.to} className="flex items-center gap-1">
                {i > 0 ? <ChevronRight className="h-3 w-3 text-vt-text-soft" aria-hidden="true" /> : null}
                {i < crumbs.length - 1 ? (
                  <Link to={c.to} className="rounded-sm hover:text-vt-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vt-primary-400">
                    {c.label}
                  </Link>
                ) : (
                  <span className="text-vt-text-muted">{c.label}</span>
                )}
              </span>
            ))}
          </nav>
          <h1 className="truncate text-vt-20 font-semibold tracking-[-0.01em] text-vt-text">{title}</h1>
        </div>

        {/* Search (placeholder; cmd+K focuses) */}
        <div
          className={cn(
            'relative hidden h-9 w-72 items-center rounded-vt-sm border bg-vt-surface text-vt-13 transition-colors duration-vt md:flex',
            searchFocused
              ? 'border-vt-primary-400 ring-2 ring-vt-primary-400/30'
              : 'border-vt-border hover:border-vt-border-strong'
          )}
        >
          <Search className="ml-3 h-4 w-4 text-vt-text-soft" strokeWidth={1.75} aria-hidden="true" />
          <input
            id="vt-topbar-search"
            type="search"
            placeholder="Search residents, units, visitors…"
            aria-label="Global search"
            className="h-full flex-1 bg-transparent px-vt-2 text-vt-13 text-vt-text placeholder:text-vt-text-soft focus:outline-none"
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
          />
          <kbd className="mr-2 hidden items-center gap-0.5 rounded border border-vt-border bg-vt-surface-2 px-1.5 py-0.5 font-mono text-[10px] text-vt-text-muted lg:inline-flex">
            <span className="text-[11px]">⌘</span>K
          </kbd>
        </div>

        {/* Right actions */}
        <div className="flex items-center gap-vt-1">
          {actions}
          <Button
            variant="ghost"
            size="icon"
            aria-label={`Notifications${unread ? `, ${unread} unread` : ''}`}
            title="Notifications"
            className="relative transition-transform duration-vt hover:scale-[1.04] active:scale-[0.96]"
          >
            <Bell className="h-4 w-4" strokeWidth={1.75} />
            {unread > 0 ? (
              <span
                className="absolute right-1.5 top-1.5 inline-flex h-2 w-2 rounded-full bg-vt-alert ring-2 ring-vt-surface"
                aria-hidden="true"
              />
            ) : null}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={toggle}
            aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
            title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
            className="transition-transform duration-vt active:scale-[0.96]"
          >
            <span className="relative inline-flex h-4 w-4 items-center justify-center">
              <Sun
                className={cn(
                  'absolute h-4 w-4 transition-all duration-vt-slow',
                  theme === 'light' ? 'rotate-0 scale-100 opacity-0' : 'rotate-0 scale-100 opacity-100'
                )}
                strokeWidth={1.75}
              />
              <Moon
                className={cn(
                  'absolute h-4 w-4 transition-all duration-vt-slow',
                  theme === 'light' ? 'rotate-0 scale-100 opacity-100' : '-rotate-45 scale-75 opacity-0'
                )}
                strokeWidth={1.75}
              />
            </span>
          </Button>

          {/* Avatar menu */}
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button
                type="button"
                aria-label="Account menu"
                className="ml-1 inline-flex h-9 w-9 items-center justify-center rounded-full bg-vt-primary-100 text-vt-13 font-semibold text-vt-primary transition-transform duration-vt hover:scale-[1.05] active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vt-primary-400"
              >
                {(user?.phone ?? 'A').slice(-2).toUpperCase()}
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                align="end"
                sideOffset={8}
                className="z-50 min-w-56 overflow-hidden rounded-vt-md border border-vt-border bg-vt-surface p-1 shadow-vt-3 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
              >
                <div className="px-vt-2 py-vt-2 text-vt-12 text-vt-text-muted">
                  Signed in as
                  <div className="mt-0.5 truncate text-vt-13 font-medium text-vt-text">
                    {user?.phone ?? '—'}
                  </div>
                </div>
                <DropdownMenu.Separator className="my-1 h-px bg-vt-border" />
                <DropdownMenu.Item asChild>
                  <Link
                    to="/settings"
                    className="block cursor-pointer rounded-vt-sm px-vt-3 py-vt-2 text-vt-13 text-vt-text outline-none data-[highlighted]:bg-vt-surface-2"
                  >
                    Settings
                  </Link>
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  className="cursor-pointer rounded-vt-sm px-vt-3 py-vt-2 text-vt-13 text-vt-alert outline-none data-[highlighted]:bg-vt-alert/10"
                  onSelect={() => void logout()}
                >
                  Sign out
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </div>
      </div>
      {description ? (
        <div className="px-vt-6 pb-vt-3 text-vt-13 text-vt-text-muted">{description}</div>
      ) : null}
    </header>
  );
}
